import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';

import Push from '../../../components/push/';
import Validator, { useValidate, checkValidity } from '../../../components/validator';
import MD5SourceURL from '../../../components/policies/md5sourceurl';

import PolicyService from '../../../data/policy-service';
import Auth from '../../../data/auth-service';

import Animations from '../../../libraries/animations';

const useStyles = makeStyles(theme => ({
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    buttonGroup: {
        '& button': {
            float: 'right',
            marginLeft: theme.spacing(1),
        },
    },
    errorMessage: {
        border: `1px solid ${theme.palette.error.main}`,
        background: theme.palette.common.black,
        color: theme.palette.common.white,
        padding: theme.spacing(3),
    },
    ...Animations.Push(theme),
    warningBox: {
        border: `1px solid ${theme.palette.warning.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
}));

const empty = {
    $disabled: false,
    $urlInvalid: false,
    $verifyInProgress: false,
    port: '',
    scheme: 'http',
    url: '',
};

const FileHash = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const [errorMessage, setErrorMessage] = useState('');
    const [policy, setPolicy] = useState({});
    const [original, setOriginal] = useState({});
    const [changes, setChanges] = useState(false);
    const fileHashForm = useValidate({});
    const [forceStateUpdate, setForceStateUpdate] = useState(false);
    const [mode, setMode] = useState('filehash');
    const filehashPerms = Auth.permissions.get('FileHashPolicy');
    const [readonly, setReadonly] = useState(!filehashPerms.write);
    const [pushing, setPushing] = useState(false);

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    useEffect(() => {
        getPolicy();
    }, []);

    useEffect(() => {
        setChanges(!_isEqual(policy, original));
    }, [policy, original]);

    const getPolicy = () => {
        PolicyService.getPolicy('file_hash', params.policy).then(res => {
            setPolicy(_cloneDeep(res));
            setOriginal(_cloneDeep(res));
            setReadonly(readonly || (filehashPerms.override ? false : (!MC2CHECK && (res.source === 'mc2' || location.pathname.includes('.mc2')))));
        }, err => {
            if (typeof err === 'string') {
                toast.error(err);
            }
        });
    };

    const handleInfo = () => {
        window.open('/support/filehash');
    };

    const handlePushPolicy = () => {
        setMode('push');
    };

    const handleDeployPolicy = (sensorList) => {
        setPushing(true);
        PolicyService.updatePolicy('file_hash', params.policy, policy).then(res => {
            toast.success('Policy saved successfully');
            getPolicy();
            setMode('filehash');

            if (sensorList.length || Object.keys(sensorList).length) {
                PolicyService.assignPolicy('file_hash', params.policy, sensorList).then(res => {
                    toast.success('Policy pushed to new sensor(s) successfully');
                }, err => {
                    if (typeof err === 'string') {
                        toast.error('Pushing policy to new sensor(s) failed : ' + err);
                    } else {
                        toast.error(err.message + ', ' + err.error);
                    }
                });
            }
        }, err => {
            if (err) {
                toast.error(err.message + ': ' + err.error);
            }
        }).finally(() => { setPushing(false) });
    };

    const handleRevertPolicy = () => {
        getPolicy();
    };

    const handleAddMD5Source = () => {
        const p = { ...policy };
        p.data.sources.push(_cloneDeep({ ...empty }));
        setPolicy(p);
    };

    const handleVerifyMD5Source = (v) => {
        PolicyService.verifyFileHash(params.policy, v).then(() => {
            toast.success('File hash verified');
        }, err => {
            if (err.message.length > 50) {
                setErrorMessage(err.message);
            } else {
                toast.error(<Box>{err.message}</Box>);
            }
        });
    };

    const handleUpdateMD5Source = (v, i) => {
        const p = { ...policy };
        p.data.sources[i].url = v;
        setPolicy(p);
    };

    const handleDeleteMD5Source = (i) => {
        const p = { ...policy };
        p.data.sources.splice(i, 1);
        setPolicy(p);
    };

    const handleClearError = () => {
        setErrorMessage('');
    };

    const handleUpdateValidity = (validID, valid) => {
        fileHashForm.set({ ...fileHashForm.get(), [validID]: valid });
    };

    const handleUnmount = (validID) => {
        const f = fileHashForm.get();
        delete f[validID];
        fileHashForm.set(f);
        setForceStateUpdate(!forceStateUpdate);
    };

    const handleModeChange = () => {
        setMode('filehash');
    };

    return (
        <Grid container spacing={2}>
            <Grid container item xs={12} style={{ display: mode === 'push' ? 'none' : 'flex' }}>
                <Grid item xs={12} className={classes.buttonGroup}>
                    {!MC2CHECK && policy.source === 'mc2' && <Box mb={1} className={classes.warningBox} display="inline-block">
                        You cannot modify or push a policy assigned by an MC2.
                    </Box>}
                    {!readonly && <Button variant="contained" color="secondary" aria-label="revert" onClick={handleRevertPolicy} disabled={!MC2CHECK && policy.source === 'mc2'}>Revert</Button>}
                    {filehashPerms.admin && <Button
                        variant="contained"
                        color="primary"
                        aria-label="push"
                        onClick={handlePushPolicy}
                        disabled={!checkValidity(fileHashForm.get()) || (!MC2CHECK && policy.source === 'mc2')}
                        className={clsx({
                            [classes.pushAnimation]: changes,
                        })}
                    >Push
                    </Button>}
                    {!readonly && <Button
                        variant="contained"
                        color="primary"
                        aria-label="md5 source url"
                        onClick={handleAddMD5Source}
                        disabled={!MC2CHECK && policy.source === 'mc2'}
                    >
                        <AddIcon /> MD5 Source URL
                    </Button>}

                    <Button variant="contained" color="primary" aria-label="info" onClick={handleInfo}><InfoIcon /></Button>
                </Grid>
            </Grid>
            <Grid item xs={12} style={{ display: mode === 'push' ? '' : 'none' }}>
                {filehashPerms.admin && <Push
                    policyName={params.policy}
                    policyType="file_hash"
                    onDeploy={handleDeployPolicy}
                    closePush={handleModeChange}
                    pushing={pushing}
                    permName={'FileHashPolicy'}
                />}
            </Grid>
            <Grid container item xs={12} spacing={2} style={{ display: mode === 'filehash' ? '' : 'none' }}>
                <Grid item xs={8}>
                    <Paper elevation={3}>
                        <Box p={2} display={policy.data && policy.data.sources.length ? 'flex' : 'none'}>
                            <Grid item xs={12}>
                                {policy.data && policy.data.sources.map((v, i) => {
                                    return (
                                        <MD5SourceURL
                                            disabled={readonly}
                                            key={'md5sourcelist-' + i}
                                            index={i}
                                            val={v.url}
                                            handleVerify={handleVerifyMD5Source}
                                            handleUpdate={handleUpdateMD5Source}
                                            handleDelete={handleDeleteMD5Source}
                                            handleValidation={handleUpdateValidity}
                                            handleUnmount={handleUnmount}
                                        />
                                    );
                                })}
                            </Grid>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={4}>
                    <Box className={classes.errorMessage} display={errorMessage ? 'block' : 'none'}>
                        <Box style={{ wordBreak: 'break-all' }}>{errorMessage}</Box>
                        <Box mt={2} width="100%" display="flex" justifyContent="flex-end">
                            <Button color="primary" variant="contained" onClick={handleClearError}>Ok</Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default FileHash;
